import React from 'react';
import { Link } from 'react-router-dom';

import { type Post } from '@/api-client/posts';
import { OptimizedImage } from '@/components/post/OptimizedImage';
import { AnimateOnScroll } from '@/components/ui/AnimateOnScroll';
import { formatDate } from '@/lib/formatDate';

interface PostCardProps {
  post: Post;
  linkState?: unknown;
}

/** The one article card used on every listing page. */
const PostCard: React.FC<PostCardProps> = ({ post, linkState }) => (
  <Link
    to={`/blog/${post.slug}`}
    state={linkState}
    className="group relative flex flex-col h-full overflow-hidden border border-neutral-800 bg-neutral-900 hover:border-neon-red hover:shadow-glow transition-all duration-300 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black"
  >
    <div className="relative h-48 overflow-hidden">
      {post.thumbnailUrl ? (
        <OptimizedImage
          src={post.thumbnailUrl}
          alt={post.title}
          className="h-full w-full"
          objectFit="cover"
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-neutral-800 via-neutral-900 to-black" />
      )}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition-colors duration-300" />
    </div>

    <div className="flex flex-col flex-1 gap-3 p-5">
      <PostMeta category={post.category} date={post.date} />
      <h3 className="text-xl font-bold text-white uppercase leading-tight group-hover:text-neon-red transition-colors duration-300">
        {post.title}
      </h3>
      {post.excerpt && (
        <p className="text-sm font-body text-neutral-300 leading-relaxed line-clamp-3 group-hover:text-neutral-100 transition-colors duration-300">
          {post.excerpt}
        </p>
      )}
      <span className="mt-auto flex items-center gap-1 group-hover:gap-2 font-mono text-sm font-bold uppercase tracking-widest text-neon-red transition-all duration-300">
        Olvasd tovább
        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
          →
        </span>
      </span>
    </div>
  </Link>
);

/** Category + date line, shared by cards and the article page. */
export const PostMeta: React.FC<{
  category?: string;
  date?: string;
  size?: 'sm' | 'md';
  children?: React.ReactNode;
}> = ({ category, date, size = 'md', children }) => (
  <div
    className={`text-neutral-400 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono uppercase tracking-widest ${
      size === 'sm' ? 'text-xs' : 'text-sm'
    }`}
  >
    {children ?? (category && <span className="text-neon-red">{category}</span>)}
    {date && <span>{formatDate(date)}</span>}
  </div>
);

interface PostGridProps {
  posts: Post[];
  linkState?: unknown;
  /** Columns on large screens */
  columns?: 3 | 4;
}

export const PostGrid: React.FC<PostGridProps> = ({ posts, linkState, columns = 3 }) => (
  <div
    className={`grid gap-6 md:grid-cols-2 ${columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
  >
    {posts.map((post, index) => (
      <AnimateOnScroll
        key={post.id}
        className="h-full"
        animation="slideUp"
        delay={(index % columns) * 0.1}
        duration={0.5}
      >
        <PostCard post={post} linkState={linkState} />
      </AnimateOnScroll>
    ))}
  </div>
);

interface PostTeaserProps {
  post: Post;
  linkState?: unknown;
  /** Bigger type for the first story of the home page */
  size?: 'lg' | 'md';
}

/** Big card with the title over the image: the lead story of a home page block. */
export const LeadPostCard: React.FC<PostTeaserProps> = ({ post, linkState, size = 'lg' }) => (
  <Link
    to={`/blog/${post.slug}`}
    state={linkState}
    className={`group relative flex h-full flex-col justify-end overflow-hidden border border-neutral-800 bg-neutral-900 hover:border-neon-red transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
      size === 'lg' ? 'min-h-[22rem] md:min-h-[28rem]' : 'min-h-[18rem] md:min-h-[22rem]'
    }`}
  >
    {post.thumbnailUrl ? (
      <OptimizedImage
        src={post.thumbnailUrl}
        alt=""
        className="!absolute inset-0 h-full w-full transition-transform duration-700 ease-smooth motion-safe:group-hover:scale-105"
        objectFit="cover"
      />
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 via-neutral-900 to-black" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/0" />

    <div className="relative flex flex-col gap-3 p-5 md:p-8">
      <PostMeta category={post.category} date={post.date} />
      <h3
        className={`font-bold text-white uppercase leading-tight md:leading-tight tracking-tight group-hover:text-neon-red transition-colors duration-300 ${
          size === 'lg' ? 'text-2xl md:text-4xl' : 'text-2xl md:text-3xl'
        }`}
      >
        {post.title}
      </h3>
      {post.excerpt && (
        <p className="max-w-2xl text-sm md:text-base font-body text-neutral-300 leading-relaxed line-clamp-2">
          {post.excerpt}
        </p>
      )}
    </div>
  </Link>
);

/** One line of a headline list: small image, category + date, title. */
export const CompactPostItem: React.FC<Omit<PostTeaserProps, 'size'>> = ({ post, linkState }) => (
  <Link
    to={`/blog/${post.slug}`}
    state={linkState}
    className="group flex gap-4 py-4 first:pt-0 last:pb-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black"
  >
    {/* Same hover as the lead card: the image zooms in and gets a red frame */}
    <div className="relative h-20 w-28 shrink-0 overflow-hidden bg-neutral-900 md:h-24 md:w-36">
      {post.thumbnailUrl ? (
        <OptimizedImage
          src={post.thumbnailUrl}
          alt=""
          className="h-full w-full transition-transform duration-700 ease-smooth motion-safe:group-hover:scale-110"
          objectFit="cover"
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-neutral-800 via-neutral-900 to-black transition-transform duration-700 ease-smooth motion-safe:group-hover:scale-110" />
      )}
      <span
        className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-neutral-800 transition-shadow duration-300 group-hover:ring-2 group-hover:ring-neon-red"
        aria-hidden="true"
      />
    </div>
    <div className="flex min-w-0 flex-col gap-2">
      <PostMeta category={post.category} date={post.date} size="sm" />
      <h3 className="text-base md:text-lg font-bold text-white uppercase leading-snug line-clamp-3 group-hover:text-neon-red transition-colors duration-200">
        {post.title}
      </h3>
    </div>
  </Link>
);
